/**
 * Die Suche ueber die lokale Kopie (KONZEPT.md §10 -- „Wo ist …?").
 *
 * Offline gibt es keine Volltextsuche von Postgres. Diese Funktion bildet nach,
 * was der Server tut (`core/db/src/stammdaten.ts`, `suchBedingung` und
 * `suchRang`), so weit es ohne Woerterbuch geht:
 *
 * - Jedes Wort der Eingabe muss in einem der Felder vorkommen -- als
 *   Wortteil, nicht nur am Wortanfang. „Leitung" findet damit „Leitungen" und
 *   „Verlaengerungsleitung".
 * - Umlaute und ß zaehlen wie ihre Umschreibung: „Feuerloescher" findet
 *   „Feuerlöscher" und umgekehrt.
 * - Eine reine Nummer sucht in den Ziffern der Inventarnummer; die, die auf
 *   die Eingabe **enden**, stehen vorn. „9281" meint 0141-029281.
 *
 * Rein und ohne Datenbank: Sie laeuft im Browser bei jedem Tastendruck und ist
 * in Millisekunden pruefbar.
 */

import type { Artikel, ArtikelMitZustand, Zustand } from "@thw/contracts";

/** Vergleichbare Schreibweise: klein, Umlaute umschrieben. */
export function vereinheitlichen(text: string): string {
  return text
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss");
}

/**
 * Die Ziffern einer Eingabe -- aber nur, wenn sie eine Nummer **ist**.
 *
 * Dieselbe Regel wie auf dem Server: Aus „Leitungsroller 50" wird keine
 * Ziffernsuche „50", sonst traefe sie jeden Wagenheber mit einer 50 in der
 * Nummer.
 */
export function nummernZiffern(text: string): string | null {
  if (!/^[\d\s\-/.]+$/.test(text)) return null;
  const ziffern = text.replace(/\D/g, "");
  return ziffern.length >= 2 ? ziffern : null;
}

/** Die Felder, in denen gesucht wird -- dieselben wie im Suchvektor des Servers. */
function suchtext(a: Artikel): string {
  return vereinheitlichen(
    [
      a.bezeichnung,
      a.inventarnummer,
      a.sachnummer,
      a.kennzeichen,
      a.geraetenummer,
      a.hersteller,
      a.einheit_zustaendig,
      a.beschreibung,
    ]
      .filter((t) => t !== null)
      .join(" "),
  );
}

/** Nur Buchstaben und Ziffern -- fuer Kennzeichen und Nummern mit Trennzeichen. */
function ohneTrenner(text: string): string {
  return text.replace(/[^a-z0-9]/g, "");
}

export interface SuchOptionen {
  limit: number;
  /** Archivierte sind standardmaessig ausgeblendet (KONZEPT.md §7.2). */
  mit_archivierten?: boolean;
}

export interface SuchErgebnis {
  treffer: ArtikelMitZustand[];
  gesamt: number;
}

/**
 * Sucht im lokalen Bestand.
 *
 * `zustaende` fehlt fuer einen Artikel nur, solange der Abgleich ihn noch
 * nicht durchgerechnet hat. Solche Artikel werden uebergangen statt mit einem
 * erfundenen Zustand gezeigt.
 */
export function suchen(
  artikel: Iterable<Artikel>,
  zustaende: ReadonlyMap<string, Zustand>,
  eingabe: string,
  optionen: SuchOptionen,
): SuchErgebnis {
  const text = eingabe.trim();
  if (text === "") return { treffer: [], gesamt: 0 };

  const ziffern = nummernZiffern(text);
  const woerter = vereinheitlichen(text)
    .split(/\s+/)
    .filter((w) => w !== "");
  const ganz = vereinheitlichen(text);

  const gefunden: { eintrag: ArtikelMitZustand; rang: number }[] = [];

  for (const a of artikel) {
    const zustand = zustaende.get(a.id);
    if (zustand === undefined) continue;
    if (!optionen.mit_archivierten && zustand.archiviert) continue;

    const nummer = a.inventarnummer.replace(/\D/g, "");
    const inhalt = suchtext(a);
    // „THW81234" soll das Kennzeichen „THW-81234" finden.
    const kompakt = ohneTrenner(inhalt);
    const bezeichnung = vereinheitlichen(a.bezeichnung);

    const passt =
      (ziffern !== null && nummer.includes(ziffern)) ||
      woerter.every((w) => {
        if (inhalt.includes(w)) return true;
        const w2 = ohneTrenner(w);
        return w2 !== "" && kompakt.includes(w2);
      });
    if (!passt) continue;

    let rang: number;
    if (ziffern !== null) {
      rang = nummer.endsWith(ziffern) ? 0 : nummer.includes(ziffern) ? 1 : 2;
    } else {
      rang = bezeichnung.includes(ganz) ? 0 : 1;
    }
    gefunden.push({ eintrag: { artikel: a, zustand }, rang });
  }

  gefunden.sort(
    (x, y) =>
      x.rang - y.rang ||
      x.eintrag.artikel.bezeichnung.localeCompare(y.eintrag.artikel.bezeichnung, "de"),
  );

  return {
    treffer: gefunden.slice(0, optionen.limit).map((g) => g.eintrag),
    gesamt: gefunden.length,
  };
}
